import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";

import { Button, ErrorState, Notice, PageHeader, RouteTabs, Select, cn, focusRing } from "../../../../components/ui";
import { useBusinessAuth } from "../../../context/BusinessAuthContext";
import { businessApi } from "../../../../services/api";
import AddMissingPeriodDialog, { windowDates } from "./AddMissingPeriodDialog";
import { HISTORY_PATH, HR_PAYROLL_BASE, SCHEDULES_PATH, WAITING_PATH, periodPath } from "./hrPayroll";

/** Whether this member may change payroll (`hr:payroll:manage`). The server enforces it on every action. */
export function useCanManagePayroll() {
  const auth = useBusinessAuth();
  const isOwner = String(auth?.role || "").toUpperCase() === "OWNER";
  return isOwner || Boolean(auth?.hasPermission?.("hr:payroll:manage"));
}

/** The business owner: the only person who activates, replaces, restores or archives a pay schedule (server-enforced). */
export function useIsOwner() {
  const auth = useBusinessAuth();
  return String(auth?.role || "").toUpperCase() === "OWNER";
}

/**
 * HR › Payroll. One workspace, four real routes:
 *   Work               — periods HR acts on next (release, returned, calculated, preparing, not started, upcoming)
 *   Waiting on Finance — periods with Finance (reviewing, or paying salaries after release)
 *   History            — settled and cancelled periods, searchable and paged
 *   Pay schedules      — the schedules that create the periods (Payroll Gate 3)
 * Each lane asks the server for its own periods; the pay schedule filter (?schedule=) applies to all three lanes.
 * Each period opens at /business/hr/payroll/periods/:periodId; each schedule at /business/hr/payroll/schedules/:id.
 */
export default function HRPayrollLayout() {
  const canManage = useCanManagePayroll();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const scheduleId = searchParams.get("schedule") || "";

  const [schedules, setSchedules] = useState([]);
  const [laneCounts, setLaneCounts] = useState(null);
  const [missing, setMissing] = useState(null);
  const [unassigned, setUnassigned] = useState([]);
  const [failed, setFailed] = useState(false);
  const [version, setVersion] = useState(0);
  const [notice, setNotice] = useState(null);
  const [creating, setCreating] = useState(false);

  // Lane counts for the tabs come from the server, with the same schedule filter as the lanes.
  useEffect(() => {
    let live = true;
    businessApi.getPayrollPeriods({ limit: 1, ...(scheduleId ? { scheduleId } : {}) })
      .then((res) => { if (live) { setLaneCounts(res?.laneCounts || null); setFailed(false); } })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [scheduleId, version]);

  useEffect(() => {
    let live = true;
    businessApi.getPaySchedules({ status: "active,archived" })
      .then((res) => { if (live) setSchedules(res?.data?.schedules || []); })
      .catch(() => { if (live) setSchedules([]); });
    return () => { live = false; };
  }, [version]);

  // Payroll Gate 1: pay schedule windows since pay started that no period covers. Shown to everyone who reads payroll;
  // only someone who manages payroll can add one.
  const loadMissing = useCallback(() => {
    businessApi.getMissingPayrollPeriods()
      .then((res) => setMissing(res?.data || null))
      .catch(() => setMissing(null));
  }, []);

  useEffect(() => {
    loadMissing();
  }, [loadMissing, version]);

  // Payroll Gate 2: employees with pay but no pay schedule are paid by no payroll. Said here, where payroll is run.
  useEffect(() => {
    let live = true;
    businessApi.getEmployeesWithoutPaySchedule()
      .then((res) => { if (live) setUnassigned(res?.data?.employees || []); })
      .catch(() => { if (live) setUnassigned([]); });
    return () => { live = false; };
  }, [pathname, version]);

  // A confirmation belongs to the lane where the action happened.
  useEffect(() => {
    setNotice(null);
  }, [pathname]);

  const reload = useCallback(() => setVersion((value) => value + 1), []);
  const setSchedule = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set("schedule", value); else next.delete("schedule");
    next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const missingWindows = missing?.windows || [];
  const addable = missingWindows.filter((item) => item.state === "missing");
  const onSchedulesTab = pathname.startsWith(SCHEDULES_PATH);
  // Archived schedules only matter here for their history; the filter lists what has periods to show.
  const filterable = schedules.filter((row) => row.status === "active" || row.usage?.periods || row.status === "archived");
  const withSchedule = (path) => (scheduleId ? `${path}?schedule=${scheduleId}` : path);

  const context = useMemo(
    () => ({ scheduleId, version, reload, setNotice, canManage, schedules }),
    [scheduleId, version, reload, canManage, schedules]
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payroll"
        description="Prepare each pay period, send it to Finance for approval, then release the payslips."
        actions={canManage && missingWindows.length && !onSchedulesTab ? (
          <Button icon={Plus} onClick={() => setCreating(true)}>Add missing period</Button>
        ) : null}
        navigation={
          <RouteTabs
            label="Payroll"
            items={[
              { to: withSchedule(HR_PAYROLL_BASE), label: "Work", count: Math.max((laneCounts?.work || 0) - (laneCounts?.upcoming || 0), 0), end: true },
              { to: withSchedule(WAITING_PATH), label: "Waiting on Finance", count: laneCounts?.waiting || 0 },
              { to: withSchedule(HISTORY_PATH), label: "History" },
              { to: SCHEDULES_PATH, label: "Pay schedules" },
              { to: `${HR_PAYROLL_BASE}/corrections`, label: "Corrections" },
            ]}
          />
        }
      />

      {!onSchedulesTab && filterable.length > 1 ? (
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="payroll-schedule-filter" className="text-label text-secondary">Pay schedule</label>
          <Select id="payroll-schedule-filter" className="w-auto min-w-56" value={scheduleId} onChange={(event) => setSchedule(event.target.value)}>
            <option value="">All pay schedules</option>
            {filterable.map((row) => (
              <option key={row.id} value={row.id}>{row.name}{row.status === "archived" ? " (archived)" : ""}</option>
            ))}
          </Select>
          {scheduleId ? <Button size="sm" variant="quiet" onClick={() => setSchedule("")}>Show all</Button> : null}
        </div>
      ) : null}

      {addable.length && !onSchedulesTab ? (
        <Notice tone="warning" title={`${addable.length} payroll period${addable.length === 1 ? " is" : "s are"} missing`}>
          {windowDates(addable[0])}{addable.length > 1 ? ` and ${addable.length - 1} more` : ""} {addable.length === 1 ? "has" : "have"} no payroll period, so pay for those days has not been worked out.
          {canManage ? " Add the missing period to prepare it." : " Someone who manages payroll can add it."}
        </Notice>
      ) : null}

      {unassigned.length ? (
        <Notice tone="warning" title={`${unassigned.length} employee${unassigned.length === 1 ? " has" : "s have"} pay but no pay schedule`}>
          No payroll pays {unassigned.length === 1 ? "them" : "these employees"} until {unassigned.length === 1 ? "they are" : "each is"} on a pay schedule:{" "}
          {unassigned.slice(0, 5).map((employee, index) => (
            <span key={employee.memberId}>
              {index ? ", " : ""}
              <Link to={`/business/hr/employees/${employee.memberId}/pay`} className={cn("rounded-control font-medium text-accent underline", focusRing)}>{employee.name}</Link>
            </span>
          ))}
          {unassigned.length > 5 ? ` and ${unassigned.length - 5} more` : ""}.
          {canManage ? " Open the employee and choose a pay schedule under Pay, or add several at once from a pay schedule's Employees." : " Someone who manages payroll can choose one."}
        </Notice>
      ) : null}

      {notice ? (
        <Notice tone={notice.tone || "success"} role="status">
          {notice.text}
        </Notice>
      ) : null}

      {failed && !onSchedulesTab ? (
        <ErrorState title="Payroll couldn't be loaded" description="Check your connection and try again." onRetry={reload} />
      ) : (
        <Outlet context={context} />
      )}

      {creating ? (
        <AddMissingPeriodDialog
          missing={missing}
          onClose={() => setCreating(false)}
          onCreated={async (period) => {
            setCreating(false);
            reload();
            if (period?.id) navigate(periodPath(period.id));
            else setNotice({ tone: "success", text: "Payroll period added." });
          }}
        />
      ) : null}
    </div>
  );
}
